import { LIST_ROWS_PER_PAGE } from '../constants';
import axiosInstance from './api';
import { formatDate, MOMENT_FORMATS } from '../utils/dateFormat';

const getApiErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  return error.message || error?.error?.message || error?.data?.message || fallback;
};

export const formatVettingDateTime = (value) => {
  if (!value) return '-';
  return formatDate(value, MOMENT_FORMATS.DATETIME) || String(value).trim();
};

export const normalizeVettingApplication = (application = {}) => {
  const user = application.user ?? {};
  const applicantName = application.applicantName ?? user.fullName ?? '';
  const applicantEmail = application.applicantEmail ?? user.userEmail ?? '';
  const vettedSubscription = user.vettedSubscription ?? null;
  const vettedSubscriptionProductName =
    vettedSubscription?.product?.name != null ? String(vettedSubscription.product.name).trim() : '';

  return {
    ...application,
    id: application.id,
    userId: application.userId ?? user.id ?? null,
    fullName: applicantName,
    applicantName,
    userEmail: applicantEmail,
    applicantEmail,
    submittedAt: application.createdAt ?? '',
    avatarUrl: application.avatarUrl ?? user.avatarUrl ?? null,
    gender: application.gender ?? user.gender ?? '',
    profession: application.profession ?? user.profession ?? '',
    phoneNumber: application.phoneNumber ?? user.phoneNumber ?? user.phone ?? '',
    interests: Array.isArray(application.interests)
      ? application.interests
      : Array.isArray(user.interests)
        ? user.interests
        : [],
    location: application.location ?? user.location ?? application.city ?? '',
    vettedSubscription,
    vettedSubscriptionProductName: vettedSubscriptionProductName || null,
  };
};

export const normalizeVettingApplicationsListResponse = (payload, params = {}) => {
  const root = payload?.data ?? payload ?? {};
  const applications = (root.applications ?? []).map(normalizeVettingApplication);
  const pagination = root.pagination ?? {};

  return {
    applications,
    total: Number(pagination.total ?? root.total ?? applications.length),
    page: Number(pagination.page ?? params.page ?? 1),
    limit: Number(pagination.limit ?? params.limit ?? LIST_ROWS_PER_PAGE),
  };
};

export const normalizeVettingApplicationDetailResponse = (payload) => {
  const root = payload?.data ?? payload ?? {};
  const application = root.application ? normalizeVettingApplication(root.application) : null;

  return {
    application,
    responses: Array.isArray(root.responses) ? root.responses : [],
    decisionLogs: Array.isArray(root.decisionLogs) ? root.decisionLogs : [],
  };
};

export const normalizeVettingConfigResponse = (payload) => {
  const root = payload?.data ?? payload ?? {};

  return {
    active: root.active ?? null,
    history: Array.isArray(root.history) ? root.history : [],
  };
};

/** GET /admin/vetting/applications — Query: page, limit, type?, subscribedOnly?, search? */
export const listVettingApplications = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/admin/vetting/applications', {
      params,
    });

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to fetch vetting applications'),
      error,
    };
  }
};

export const getVettingApplicationById = async (applicationId) => {
  try {
    const response = await axiosInstance.get(`/admin/vetting/applications/${applicationId}`);

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to fetch vetting application detail'),
      error,
    };
  }
};

export const getVettingConfig = async () => {
  try {
    const response = await axiosInstance.get('/admin/vetting/config');

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to fetch vetting config'),
      error,
    };
  }
};

export const updateVettingConfig = async (payload) => {
  try {
    const response = await axiosInstance.post('/admin/vetting/config', payload);

    return {
      success: true,
      data: response.data,
      message: response.data?.message || 'Vetting config updated',
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to update vetting config'),
      error,
    };
  }
};

export const overrideVettingApplicationDecision = async (applicationId, payload) => {
  try {
    const response = await axiosInstance.patch(
      `/admin/vetting/applications/${applicationId}/decision`,
      payload,
    );

    return {
      success: true,
      data: response.data,
      message: response.data?.message || 'Decision overridden successfully',
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to override decision'),
      error,
    };
  }
};

export const rescoreVettingApplication = async (applicationId) => {
  try {
    const response = await axiosInstance.post(
      `/admin/vetting/applications/${applicationId}/rescore`,
    );

    return {
      success: true,
      data: response.data,
      message: response.data?.message || 'Rescore triggered',
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to trigger rescore'),
      error,
    };
  }
};
